// PlayersListPanel — rebuilt 2026-05-16 for the Entity Redesign
// (Step 2). The cosmetic-only version was replaced with a high-
// density mobile-first card that exposes the new Tier-1 God-Mode
// verbs through a per-row action sheet (EntityActionSheet), plus a
// bulk-select mode (cap=50 per the blueprint sign-off).
//
// The information density of each card surfaces the things a support
// agent would otherwise have to click into the profile to see:
//
//   • Avatar + display-name + @handle + #id + status pill
//   • Verification chips: email/phone/telegram (✅ or grey-✖)
//   • Test-account flag (orange beaker chip)
//   • Wallet balance + joined-date strip
//   • Ownership chain ("under @agent_kumar" or "platform direct")
//   • Contact line: phone · email (each tap-to-copy)
//   • Lockout warning ("Locked until 14:23" — amber chip)
//
// Per-row actions live behind a "•••" kebab that opens the action
// sheet with:
//
//   • Profile / Wallet credit / Wallet debit / Reassign agent
//   • Suspend / Activate / Ban / Restore
//   • Force-verify email / Force-verify phone / Unlink Telegram
//   • Clear lockout / Force logout / Reset password
//   • Mark/Unmark test account (admin only)
//   • Delete (admin only)
//
// Bulk mode: tap "Select" → cards show a checkbox. Selected count
// rendered in a sticky footer. Operations: bulk suspend (cap 50) or
// bulk reassign. Disabled while no rows selected.
//
// Light + dark designed.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { levelName } from '../../lib/levelNames';
import { Link, useNavigate, type NavigateFunction } from 'react-router-dom';
import { motion } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import AgentReassignSheet from '../../components/AgentReassignSheet';
import CreatePlayerModal from '@baaligames/shared-ui/admin/components/CreatePlayerModal';
import EntityActionSheet, { type ActionGroup } from '../../components/EntityActionSheet';
import DirectWalletAdjustModal from '../../components/DirectWalletAdjustModal';
import { ApiError } from '@baaligames/shared-ui/lib/api';

type UserRow = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  profile_image_url: string | null;
  status: 'pending' | 'active' | 'suspended' | 'banned';
  email_verified_at: string | null;
  phone_verified_at: string | null;
  telegram_verified_at: string | null;
  failed_login_attempts: number;
  locked_until: string | null;
  is_test: 0 | 1 | boolean;
  created_at: string;
  last_login_at: string | null;
  wallet_balance: number;
  // Phase 11B
  agent_id: number | null;
  agent_username: string | null;
  relation_to_target?: 'direct' | 'indirect';
  // Phase B (plan §4.2): "drifting" = this week's average stake is below
  // 70% of their 30-day average. Computed nightly on player_luck's '*' row
  // and joined in by UserModel.listForAdmin; optional until that ships.
  is_drifting?: 0 | 1 | boolean | null;
};

type ListResp = { rows: UserRow[]; total: number; limit: number; offset: number };

const PAGE_SIZE = 24;
const BULK_CAP = 50; // blueprint Section 7 sign-off

const STATUS_OPTS: Array<{ key: '' | UserRow['status']; label: string }> = [
  { key: '',          label: 'All' },
  { key: 'active',    label: 'Active' },
  { key: 'pending',   label: 'Pending' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'banned',    label: 'Banned' },
];

const STATUS_PILL: Record<UserRow['status'], string> = {
  active:    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
  pending:   'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
  suspended: 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/30',
  banned:    'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/30',
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

export default function PlayersListPanel() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const isAdmin = admin?.role === 'admin';
  const [data, setData] = useState<ListResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | UserRow['status']>('');
  const [testFilter, setTestFilter] = useState<'' | 'hide_test' | 'only_test'>('hide_test');
  // Phase B: "Drifting" chip → SERVER-side `?drifting=1` (a client-side
  // filter would break pagination — the map's C10 warning).
  const [drifting, setDrifting] = useState(false);
  // hierarchy filter (Phase 11B carry-over)
  const [underAgent, setUnderAgent] = useState<number | ''>('');
  const [relation, setRelation] = useState<'direct' | 'indirect' | 'all'>('all');
  const [agentOpts, setAgentOpts] = useState<Array<{ id: number; username: string; level: number }>>([]);
  const [reassigning, setReassigning] = useState<UserRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(0);
  const [actioning, setActioning] = useState<UserRow | null>(null);
  const [adjusting, setAdjusting] = useState<UserRow | null>(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get<{ rows: Array<{ id: number; username: string; level: number }> }>(
          '/admin/hierarchy/agents?limit=200'
        );
        setAgentOpts(r.rows);
      } catch { /* hierarchy is best-effort */ }
    })();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const q = new URLSearchParams();
      q.set('limit', String(PAGE_SIZE));
      q.set('offset', String(page * PAGE_SIZE));
      if (debounced) q.set('search', debounced);
      if (statusFilter) q.set('status', statusFilter);
      // Only /admin/users honours `drifting`; the hierarchy endpoint
      // (/admin/hierarchy/users-under) ignores it, so the chip is disabled
      // while an agent filter is active and the param is never sent there.
      if (drifting && underAgent === '') q.set('drifting', '1');
      let url: string;
      if (underAgent !== '') {
        q.set('agent_id', String(underAgent));
        q.set('relation', relation);
        url = `/admin/hierarchy/users-under?${q.toString()}`;
      } else {
        url = `/admin/users?${q.toString()}`;
      }
      const d = await adminApi.get<ListResp>(url);
      setData(d);
    } catch (e) { setErr((e as ApiError).message || 'Failed to load users'); }
    finally { setLoading(false); }
  }, [debounced, statusFilter, page, underAgent, relation, drifting]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(0); }, [debounced, statusFilter, underAgent, relation, testFilter, drifting]);
  // Drifting + "Under agent" cannot be combined (the hierarchy endpoint has no
  // drifting filter) — picking an agent switches the chip off so the list
  // never claims a filter the server did not apply.
  useEffect(() => { if (underAgent !== '') setDrifting(false); }, [underAgent]);
  // Reset selection when the page or filters change.
  useEffect(() => { setSelected(new Set()); setBulkResult(null); }, [debounced, statusFilter, page, underAgent, relation, bulkMode, testFilter, drifting]);

  // Test-flag filter is applied client-side — backend doesn't (yet)
  // have a `?is_test=` filter, and we want zero-latency toggling.
  const visibleRows = useMemo(() => {
    if (!data) return [];
    if (testFilter === 'hide_test') return data.rows.filter((r) => !r.is_test);
    if (testFilter === 'only_test') return data.rows.filter((r) =>  r.is_test);
    return data.rows;
  }, [data, testFilter]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const showingFrom = data ? data.offset + 1 : 0;
  const showingTo   = data ? Math.min(data.offset + data.rows.length, data.total) : 0;

  const headerSubtitle = useMemo(() => {
    if (loading && !data) return 'Loading…';
    if (!data) return '';
    if (data.total === 0) return 'No players match';
    return `Showing ${showingFrom}–${showingTo} of ${data.total.toLocaleString('en-IN')}`;
  }, [data, loading, showingFrom, showingTo]);

  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size >= BULK_CAP) {
        // hard cap from blueprint sign-off
        return next;
      } else next.add(id);
      return next;
    });
  }

  async function runRowAction(u: UserRow, fn: () => Promise<void>) {
    await fn();
    await load();
  }

  async function bulkSuspend() {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true); setBulkResult(null);
    const ids = Array.from(selected);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await adminApi.post(`/admin/users/${id}/status`, { status: 'suspended', reason: 'bulk-suspend' });
        ok++;
      } catch { fail++; }
    }
    setBulkBusy(false);
    setBulkResult(`Suspended ${ok} of ${ids.length}${fail ? ` (${fail} failed)` : ''}`);
    setSelected(new Set());
    await load();
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4 pb-24">
      {/* Header — compact on mobile (icon-only actions), expands on sm+ */}
      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  className="px-2 lg:px-4 pt-3 lg:pt-2">
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="grid place-items-center w-9 h-9 sm:w-10 sm:h-10 rounded-xl shrink-0
                           bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100
                           dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30">
            <Icon name="group" size={20} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-display font-semibold text-neutral-900 dark:text-white tracking-tight leading-tight">
              Players
            </h1>
            <p className="text-[11.5px] sm:text-[12px] text-arena-600 dark:text-neon-200/70 truncate">{headerSubtitle}</p>
          </div>
          {/* Bulk-mode toggle — icon-only on phones, label appears sm+ */}
          <button
            onClick={() => setBulkMode((v) => !v)}
            className={`shrink-0 inline-flex items-center gap-1 rounded-full text-[12px] font-bold transition active:scale-95
              h-9 w-9 sm:w-auto sm:px-3 justify-center
              ${bulkMode
                ? 'bg-arena-200 text-neutral-900 dark:bg-arena-700 dark:text-white ring-1 ring-inset ring-arena-300/70 dark:ring-arena-500/40'
                : 'bg-white dark:bg-arena-700/40 text-arena-700 dark:text-neon-200/85 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-700/60'}`}
            aria-label={bulkMode ? 'Stop selecting' : 'Start selecting'}
          >
            <Icon name={bulkMode ? 'checklist' : 'select_all'} size={16} />
            <span className="hidden sm:inline">{bulkMode ? 'Selecting' : 'Select'}</span>
          </button>
          <button
            onClick={() => setCreating(true)}
            className="shrink-0 inline-flex items-center gap-1 rounded-full
                       bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                       text-white text-[12px] font-bold h-9 w-9 sm:w-auto sm:px-3 justify-center
                       shadow-[0_2px_10px_rgba(139,92,246,0.45)] transition active:scale-95"
            aria-label="Add player"
          >
            <Icon name="person_add" size={16} />
            <span className="hidden sm:inline">Add player</span>
          </button>
        </div>
      </motion.div>

      {/* Sticky filter bar — search + status + test-flag + hierarchy */}
      <div className="px-2 lg:px-4 sticky top-0 z-30 -mx-2 lg:-mx-4 pt-1 pb-2 bg-neutral-50/85 dark:bg-arena-900/85 backdrop-blur supports-backdrop-blur:bg-neutral-50/70 supports-backdrop-blur:dark:bg-arena-900/70">
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center px-2 lg:px-2">
          <div className="relative flex-1 min-w-0">
            <Icon name="search" size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-arena-600 dark:text-neon-200/70 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search username, email, phone…"
              className="w-full rounded-full pl-9 pr-3 py-2 text-[13px]
                         bg-white dark:bg-arena-700/40
                         ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                         text-neutral-900 dark:text-white
                         placeholder-arena-400 dark:placeholder-white/40
                         focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400/40"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2
                                                                p-1 rounded-full hover:bg-arena-100/60 dark:hover:bg-arena-700/70">
                <Icon name="close" size={14} className="text-arena-600 dark:text-neon-200/70" />
              </button>
            )}
          </div>
          {/* Filter pills — wrap to 2 rows on mobile (so nothing gets
             cut off mid-character), revert to single-row horizontal
             scroll once we have enough width at sm+ for them to fit
             inline. The `flex-wrap` + `sm:flex-nowrap` switch is the
             load-bearing piece. */}
          <div className="flex flex-wrap sm:flex-nowrap gap-1.5 sm:overflow-x-auto sm:no-scrollbar">
            {STATUS_OPTS.map((o) => {
              const active = o.key === statusFilter;
              return (
                <button
                  key={o.key || 'all'}
                  onClick={() => setStatusFilter(o.key)}
                  className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11.5px] sm:text-[12px] font-semibold transition border
                    ${active
                      ? 'bg-violet-600 text-white border-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.35)] dark:bg-white dark:text-neutral-900 dark:border-white'
                      : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'}`}
                >
                  {o.label}
                </button>
              );
            })}
            {/* Test-flag filter */}
            {(['hide_test','','only_test'] as const).map((tf) => {
              const active = tf === testFilter;
              const label = tf === 'hide_test' ? 'Hide test' : tf === 'only_test' ? 'Only test' : 'All incl. test';
              return (
                <button key={tf || 'all_inc'} onClick={() => setTestFilter(tf)}
                  className={`shrink-0 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11.5px] sm:text-[12px] font-semibold transition border
                    ${active
                      ? 'bg-amber-500 text-white border-amber-500 dark:bg-amber-400 dark:text-arena-900 dark:border-amber-400'
                      : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'}`}>
                  {label}
                </button>
              );
            })}
            {/* Phase B: Drifting chip (plan §4.7 — "a filter chip under
                People → Players, not a Profit sub-tab"). Server-side filter. */}
            <button
              onClick={() => setDrifting((v) => !v)}
              aria-pressed={drifting}
              disabled={underAgent !== ''}
              title={underAgent !== ''
                ? 'Not available together with the "Under agent" filter — set that back to all users first'
                : 'Players betting noticeably smaller than their usual this week'}
              className={`shrink-0 inline-flex items-center gap-1 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11.5px] sm:text-[12px] font-semibold transition border
                disabled:opacity-40 disabled:cursor-not-allowed
                ${drifting
                  ? 'bg-orange-500 text-white border-orange-500 dark:bg-orange-400 dark:text-arena-900 dark:border-orange-400'
                  : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'}`}
            >
              <Icon name="trending_down" size={12} filled /> Drifting
            </button>
          </div>
        </div>
        {drifting && underAgent === '' && (
          <p className="px-2 lg:px-2 mt-1.5 text-[11px] text-orange-700 dark:text-orange-300">
            Drifting = betting noticeably smaller than usual this week (under 70% of their 30-day average stake). Worth a call from their agent.
          </p>
        )}

        {/* Hierarchy filter row */}
        <div className="px-2 lg:px-2 mt-2 flex flex-wrap gap-1.5 items-center">
          <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/55 font-bold mr-1">
            Under agent:
          </span>
          <select
            value={underAgent}
            onChange={(e) => setUnderAgent(e.target.value === '' ? '' : Number(e.target.value))}
            className="shrink-0 px-2.5 py-1 rounded-full text-[12px] font-semibold ring-1 ring-inset bg-white ring-arena-200/60 dark:bg-arena-700/40 dark:ring-arena-600/40 dark:text-neon-200/85"
          >
            <option value="">— All users (no agent filter) —</option>
            {agentOpts.map((a) => (
              <option key={a.id} value={a.id}>@{a.username} ({levelName(a.level)})</option>
            ))}
          </select>
          {underAgent !== '' && (
            <>
              <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/55 font-bold ml-1 mr-1">Relation:</span>
              {(['all','direct','indirect'] as const).map((r) => {
                const active = r === relation;
                return (
                  <button key={r} onClick={() => setRelation(r)}
                    className={`shrink-0 px-3 py-1 rounded-full text-[11.5px] font-semibold transition border capitalize
                      ${active
                        ? 'bg-cyan-600 text-white border-cyan-600 dark:bg-cyan-500 dark:border-cyan-500'
                        : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'}`}>
                    {r}
                  </button>
                );
              })}
            </>
          )}
        </div>
      </div>

      {err && (
        <div className="mx-2 lg:mx-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
          {err}
        </div>
      )}

      {/* Skeleton or grid */}
      {loading && !data && (
        <div className="px-2 lg:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-4 animate-pulse">
              <div className="h-12 mb-3 bg-arena-100/60 dark:bg-arena-800/40 rounded-xl" />
              <div className="h-4 mb-2 bg-arena-100/60 dark:bg-arena-800/40 rounded" />
              <div className="h-3 mb-2 bg-arena-100/60 dark:bg-arena-800/40 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {data && visibleRows.length === 0 && !loading && (
        <div className="mx-2 lg:mx-4 rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-10 text-center">
          <Icon name="person_search" size={32} className="text-arena-400 dark:text-neon-200/35 mx-auto mb-2" />
          <p className="text-[13px] text-arena-700 dark:text-neon-200/75 font-semibold">No players match</p>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/55 mt-0.5">
            Try a different search, status, test-flag or drifting filter.
          </p>
        </div>
      )}

      {data && visibleRows.length > 0 && (
        <div className="px-2 lg:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {visibleRows.map((u, i) => (
            <PlayerCard
              key={u.id} u={u} index={i}
              driftingFilter={drifting && underAgent === ''}
              bulkMode={bulkMode}
              selected={selected.has(u.id)}
              onToggleSelect={() => toggleSelect(u.id)}
              onOpenActions={() => setActioning(u)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="px-2 lg:px-4 flex items-center justify-end gap-2 pt-1 text-[12px] text-arena-600 dark:text-neon-200/70">
          <button disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            <Icon name="chevron_left" size={16} />
          </button>
          <span className="tabular-nums">Page {page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="grid place-items-center w-9 h-9 rounded-full
                       bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                       disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/60">
            <Icon name="chevron_right" size={16} />
          </button>
        </div>
      )}

      {/* Bulk-action sticky footer (only renders when in bulk mode) */}
      {bulkMode && (
        <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-40 w-[calc(100vw-1.5rem)] max-w-screen-md
                        rounded-2xl bg-white dark:bg-arena-800 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                        shadow-[0_10px_30px_-12px_rgba(0,0,0,0.45)] px-3 py-2.5
                        flex items-center gap-2">
          <Icon name="checklist" size={16} className="text-arena-600 dark:text-neon-200/65" />
          <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white">
            {selected.size} selected
            <span className="ml-1 text-arena-600 dark:text-neon-200/55 font-normal">(cap {BULK_CAP})</span>
          </div>
          {bulkResult && (
            <span className="text-[11.5px] text-arena-700 dark:text-neon-200/75 ml-1 truncate">
              · {bulkResult}
            </span>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelected(new Set())} disabled={selected.size === 0}
              className="text-[12px] font-semibold text-arena-700 dark:text-neon-200/75 px-2 py-1 rounded-full hover:bg-arena-50 dark:hover:bg-arena-700/60 disabled:opacity-40">
              Clear
            </button>
            <button onClick={bulkSuspend} disabled={selected.size === 0 || bulkBusy}
              className="inline-flex items-center gap-1 text-[12px] font-bold text-white px-3 py-1.5 rounded-full
                         bg-orange-600 hover:bg-orange-500 disabled:opacity-40">
              {bulkBusy ? <Icon name="progress_activity" size={12} className="animate-spin" /> : <Icon name="block" size={12} />}
              Suspend {selected.size}
            </button>
          </div>
        </div>
      )}

      {reassigning && (
        <AgentReassignSheet
          subject={{ kind: 'player', label: reassigning.username, currentParentId: reassigning.agent_id }}
          detach={{ label: 'Detach to platform', description: 'Remove agent ownership. Admin manages directly.', icon: 'shield' }}
          onClose={() => setReassigning(null)}
          onAssign={async (newParentId, reason) => {
            await adminApi.post(`/admin/users/${reassigning.id}/reassign-agent`, {
              new_agent_id: newParentId,
              reason: reason || undefined,
            });
            setReassigning(null);
            void load();
          }}
        />
      )}

      {creating && (
        <CreatePlayerModal
          mode="admin"
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); void load(); }}
        />
      )}

      {actioning && (
        <PlayerActionSheet
          u={actioning}
          isAdmin={isAdmin}
          navigate={navigate}
          onClose={() => setActioning(null)}
          onReassign={() => { const u = actioning; setActioning(null); setReassigning(u); }}
          onAdjust={() => { const u = actioning; setActioning(null); setAdjusting(u); }}
          onMutated={(u) => runRowAction(u, async () => {})}
        />
      )}
      {adjusting && (
        <DirectWalletAdjustModal
          subject={{
            kind: 'player',
            id: adjusting.id,
            username: adjusting.username,
            // Pass the rich identity fields so the modal can render
            // the "who am I crediting?" identity card with avatar +
            // display name + ownership chain (FLAW polish 2026-05-16).
            displayName: (adjusting.first_name || adjusting.last_name)
              ? `${adjusting.first_name || ''} ${adjusting.last_name || ''}`.trim()
              : adjusting.username,
            avatarUrl: adjusting.profile_image_url,
            agentId: adjusting.agent_id,
            agentUsername: adjusting.agent_username,
            walletBalance: adjusting.wallet_balance,
          }}
          onClose={() => setAdjusting(null)}
          onDone={() => { setAdjusting(null); void load(); }}
        />
      )}
    </div>
  );
}

// ─── Player card ───────────────────────────────────────────────────
//
// MOBILE-FIRST LAYOUT (rebuilt 2026-05-16)
// ────────────────────────────────────────
// The previous version had a 2-column grid for the wallet/joined strip
// and an inline REASSIGN button — both fine on desktop but the card
// ended up ~270px tall on 390px-wide phones, with the wallet/joined
// columns awkwardly narrow and a redundant CTA repeating the kebab.
//
// New structure (top → bottom):
//   1. Top row: avatar (40px on mobile, 44px ≥sm) + identity column
//      (name • @handle · #id) + status pill on the right
//   2. Chip row: verification DOTS (compact, no labels), plus
//      test / locked-out badges. Single 24-30px-tall row.
//   3. Money strip: ONE row — wallet ₹X (display font, big) on the
//      left; "Joined Nm ago" or "under @owner" on the right. No
//      2-col grid wasting horizontal space.
//   4. Contact: ONE line, truncated mid-string with ellipsis. Phone
//      on the left, email (max-60%) on the right.
//
// Bulk-mode + kebab are absolute-positioned in the corners and clear
// any text behind them with a small `pr-9` content gutter. Padding is
// `p-3 sm:p-4` — saves ~30px on phones, restores the desktop feel
// at sm+ breakpoints.
//
// REASSIGN inline button removed — it duplicates an action that's
// already one tap away in the kebab sheet. Frees ~36px per card on
// mobile and aligns with the iOS/Android "one primary affordance per
// list row" pattern.
function PlayerCard({
  u, index, driftingFilter = false, bulkMode, selected, onToggleSelect, onOpenActions,
}: {
  u: UserRow;
  index: number;
  /** The list was fetched with ?drifting=1 — every row here IS drifting by definition. */
  driftingFilter?: boolean;
  bulkMode: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onOpenActions: () => void;
}) {
  const displayName = (u.first_name || u.last_name)
    ? `${u.first_name || ''} ${u.last_name || ''}`.trim()
    : u.username;
  const emailOk    = !!u.email_verified_at;
  const phoneOk    = !!u.phone_verified_at;
  const telegramOk = !!u.telegram_verified_at;
  const locked     = !!u.locked_until && new Date(u.locked_until) > new Date();
  const isTest     = !!u.is_test;
  // listForAdmin filters on player_luck.is_drifting but does not (yet) project
  // the column, so outside the Drifting-filtered list the flag is absent;
  // inside it every row is drifting by construction.
  const isDrifting = !!u.is_drifting || driftingFilter;
  // Pretty "N days ago" for the money strip's secondary slot — kept
  // tiny so it doesn't compete with the wallet figure for attention.
  const joinedRel = relTime(u.created_at);

  function copy(text: string, e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    if (!text) return;
    try { void navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.015, 0.18) }}
      className={`group relative rounded-2xl
                  bg-white dark:bg-arena-700/40
                  ring-1 ring-inset ${selected
                    ? 'ring-violet-400/70 dark:ring-violet-400/60'
                    : 'ring-arena-200/55 dark:ring-arena-600/40'}
                  shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                  hover:-translate-y-0.5 hover:ring-cyan-300 dark:hover:ring-cyan-400/40 transition`}
    >
      {bulkMode && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSelect(); }}
          className={`absolute top-2.5 left-2.5 z-10 grid place-items-center w-6 h-6 rounded-md
                      ${selected
                        ? 'bg-violet-600 text-white ring-1 ring-violet-600'
                        : 'bg-white dark:bg-arena-800 text-arena-500 dark:text-neon-200/60 ring-1 ring-inset ring-arena-300/70 dark:ring-arena-600/40'}`}
          aria-label={selected ? 'Deselect player' : 'Select player'}
        >
          {selected && <Icon name="check" size={14} />}
        </button>
      )}
      <button
        onClick={onOpenActions}
        className="absolute top-2 right-2 z-10 grid place-items-center w-9 h-9 rounded-full
                   text-arena-500 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60
                   active:bg-arena-200/60 dark:active:bg-arena-700"
        aria-label="More actions"
      >
        <Icon name="more_horiz" size={18} />
      </button>

      <Link
        to={`/people/players/${u.id}`}
        onClick={(e) => { if (bulkMode) { e.preventDefault(); onToggleSelect(); } }}
        className={`block p-3 sm:p-4 ${bulkMode ? 'pl-10' : ''} pr-11
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50 rounded-2xl`}
      >
        {/* Row 1: avatar + identity + status pill */}
        <div className="flex items-start gap-2.5 sm:gap-3">
          <AdminAvatar url={u.profile_image_url} username={u.username} size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-1.5">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] sm:text-[14.5px] font-semibold text-neutral-900 dark:text-white truncate leading-tight">
                  {displayName}
                </div>
                {/* Phase C4 — Identity Stack line 2: @username · #id.
                    The Name line 1 above already shows display name; we
                    don't duplicate it here. Phone (line 3) lives in the
                    contact row below as tap-to-copy. */}
                <div className="text-[11px] text-arena-600 dark:text-neon-200/65 truncate leading-tight mt-0.5 font-mono">
                  @{u.username} · #{u.id}
                </div>
              </div>
              <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold capitalize ${STATUS_PILL[u.status]}`}>
                <span className="w-1 h-1 rounded-full bg-current" />
                {u.status}
              </span>
            </div>
          </div>
        </div>

        {/* Row 2: chip strip — verification DOTS + special chips */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
          <span className="inline-flex items-center gap-1 text-arena-600 dark:text-neon-200/55">
            <VerDot icon="mail" ok={emailOk} label="email" />
            <VerDot icon="call" ok={phoneOk} label="phone" />
            <VerDot icon="send" ok={telegramOk} label="telegram" />
          </span>
          {isTest && (
            <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                             bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/70
                             dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
              <Icon name="science" size={9} filled /> test
            </span>
          )}
          {locked && (
            <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                             bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60
                             dark:bg-orange-400/15 dark:text-orange-300 dark:ring-orange-400/30">
              <Icon name="lock_clock" size={9} filled /> locked
            </span>
          )}
          {isDrifting && (
            <span title="Betting noticeably smaller than their usual this week"
                  className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                             bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60
                             dark:bg-orange-400/15 dark:text-orange-300 dark:ring-orange-400/30">
              <Icon name="trending_down" size={9} filled /> drifting
            </span>
          )}
        </div>

        {/* Row 3: money strip — wallet (big) + ownership / joined (subtle) */}
        <div className="mt-2.5 pt-2.5 border-t border-arena-200/40 dark:border-arena-700/40
                        flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[14px] sm:text-[15px] font-display font-extrabold text-cyan-700 dark:text-cyan-300 tabular-nums leading-none">
              {inr(u.wallet_balance)}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">
              wallet
            </div>
          </div>
          <div className="text-right min-w-0 max-w-[55%]">
            <div className="text-[11px] font-semibold text-neutral-900 dark:text-white truncate leading-tight">
              {u.agent_username
                ? <>under <span className="text-arena-700 dark:text-neon-200/85">@{u.agent_username}</span></>
                : <span className="italic text-arena-400 dark:text-neon-200/45">platform direct</span>}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 leading-tight mt-0.5">
              joined {joinedRel}
            </div>
            <div className="text-[10px] text-arena-600 dark:text-neon-200/70 leading-tight mt-0.5" title={u.last_login_at || ''}>
              last login {u.last_login_at ? relTime(u.last_login_at) : 'never'}
            </div>
          </div>
        </div>

        {/* Row 4: contact (single row, both tap-to-copy) */}
        {(u.email || u.phone) && (
          <div className="mt-2 flex items-center gap-2 text-[10.5px] text-arena-600 dark:text-neon-200/55 leading-tight">
            {u.phone && (
              <PhoneContact phone={u.phone} name={u.username} iconSize={9}
                className="font-mono inline-flex items-center gap-0.5 shrink-0 hover:text-arena-800 dark:hover:text-neon-200/85 max-w-[40%] truncate" />
            )}
            {u.email && (
              <button onClick={(e) => copy(u.email, e)}
                className="inline-flex items-center gap-0.5 min-w-0 hover:text-arena-800 dark:hover:text-neon-200/85 flex-1 truncate justify-end">
                <Icon name="content_copy" size={9} className="shrink-0" />
                <span className="truncate">{u.email}</span>
              </button>
            )}
          </div>
        )}
      </Link>
    </motion.div>
  );
}

/**
 * VerDot — single-icon verification indicator. Lives in a row with
 * its siblings so we can show "email phone tg" at a glance without
 * the previous 3 wrappy pill chips. Tooltip-on-hover via title attr.
 */
function VerDot({ icon, ok, label }: { icon: string; ok: boolean; label: string }) {
  return (
    <span title={`${label}: ${ok ? 'verified' : 'not verified'}`}
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full
        ${ok
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30'
          : 'bg-arena-50 text-arena-400 ring-1 ring-inset ring-arena-200/50 dark:bg-arena-700/40 dark:text-neon-200/30 dark:ring-arena-600/40'}`}>
      <Icon name={icon} size={9} filled={ok} />
    </span>
  );
}

// Relative-time helper shared between cards. Compact "5m ago" / "3d ago".
function relTime(ts: string | null): string {
  if (!ts) return 'never';
  const ms = Date.now() - new Date(ts).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  return `${mo}mo ago`;
}

// ─── Action sheet wrapper ──────────────────────────────────────────

function PlayerActionSheet({
  u, isAdmin, navigate, onClose, onReassign, onAdjust, onMutated,
}: {
  u: UserRow;
  isAdmin: boolean;
  navigate: NavigateFunction;
  onClose: () => void;
  onReassign: () => void;
  onAdjust: () => void;
  onMutated: (u: UserRow) => void;
}) {
  const isActive    = u.status === 'active';
  const isBanned    = u.status === 'banned';
  const emailOk     = !!u.email_verified_at;
  const phoneOk     = !!u.phone_verified_at;
  const hasTelegram = !!u.telegram_verified_at || !!u.email_verified_at; // any TG state
  const locked      = !!u.locked_until && new Date(u.locked_until) > new Date();

  async function post(path: string, body?: unknown) {
    await adminApi.post(path, body);
    onMutated(u);
  }

  const groups: ActionGroup[] = [
    {
      title: 'Open',
      items: [
        { key: 'profile', icon: 'open_in_new', label: 'Open profile',
          hint: `View full record for @${u.username}`,
          onSelect: () => { navigate(`/people/players/${u.id}`); } },
        { key: 'reassign', icon: 'swap_horiz', label: 'Reassign agent',
          hint: u.agent_username ? `Currently under @${u.agent_username}` : 'Currently platform-direct',
          onSelect: () => { onReassign(); } },
      ],
    },
    {
      // FLAW 2 fix (2026-05-16): "God-Mode" direct wallet actions.
      // Opens the DirectWalletAdjustModal where admin picks source
      // (Platform vs Agent pool) + direction (Credit vs Debit). Single
      // entry in the action sheet — the modal carries the variants.
      title: 'Wallet',
      items: [
        { key: 'direct_adjust', icon: 'account_balance_wallet',
          label: 'Direct credit / debit',
          hint: u.agent_username
            ? `From platform or @${u.agent_username}'s pool · ledger + audit`
            : 'Platform-direct credit / debit · ledger + audit',
          onSelect: () => { onAdjust(); } },
      ],
    },
    {
      title: 'Status',
      items: [
        { key: 'activate', icon: 'play_arrow', label: 'Activate', hidden: isActive,
          onSelect: () => post(`/admin/users/${u.id}/status`, { status: 'active' }) },
        { key: 'suspend',  icon: 'block', label: 'Suspend', tone: 'danger', hidden: !isActive,
          confirm: { title: `Suspend @${u.username}?`, body: 'Player will be signed out of every device and blocked from logging in until reactivated.', cta: 'Suspend', ctaTone: 'danger' },
          onSelect: () => post(`/admin/users/${u.id}/status`, { status: 'suspended' }) },
        { key: 'ban', icon: 'gavel', label: 'Ban', tone: 'danger', hidden: isBanned,
          confirm: { title: `Ban @${u.username}?`, body: 'Stronger than suspend — used for ToS violations.', cta: 'Ban', ctaTone: 'danger' },
          onSelect: () => post(`/admin/users/${u.id}/status`, { status: 'banned' }) },
        { key: 'restore', icon: 'restart_alt', label: 'Restore to active', hidden: !isBanned,
          onSelect: () => post(`/admin/users/${u.id}/status`, { status: 'active' }) },
      ],
    },
    {
      title: 'Verification',
      items: [
        { key: 'fv_email', icon: 'mark_email_read', label: 'Force-verify email',
          hint: emailOk ? 'Already verified' : 'Stamps email_verified_at = NOW()', disabled: emailOk,
          onSelect: () => post(`/admin/users/${u.id}/force-verify-email`) },
        { key: 'fv_phone', icon: 'mark_chat_read', label: 'Force-verify phone',
          hint: phoneOk ? 'Already verified' : 'Stamps phone_verified_at = NOW()', disabled: phoneOk,
          onSelect: () => post(`/admin/users/${u.id}/force-verify-phone`) },
        { key: 'tg_unlink', icon: 'link_off', label: 'Unlink Telegram',
          hint: hasTelegram ? 'Clears chat id / username / verified_at' : 'No Telegram link to clear',
          disabled: !u.telegram_verified_at, tone: 'danger',
          confirm: { title: `Unlink Telegram for @${u.username}?`, body: 'The user will have to complete the bot conversation again to re-bind a chat id.', cta: 'Unlink', ctaTone: 'danger' },
          onSelect: () => post(`/admin/users/${u.id}/unlink-telegram`) },
      ],
    },
    {
      title: 'Sessions & security',
      items: [
        { key: 'clear_lockout', icon: 'lock_open', label: 'Clear lockout',
          hint: locked ? `Locked until ${new Date(u.locked_until!).toLocaleTimeString('en-IN')}` : 'No active lockout',
          disabled: !locked && u.failed_login_attempts === 0,
          onSelect: () => post(`/admin/users/${u.id}/clear-lockout`) },
        { key: 'force_logout', icon: 'logout', label: 'Force logout', tone: 'danger',
          hint: 'Revokes every refresh token',
          confirm: { title: `Force log @${u.username} out everywhere?`, body: 'They will be punted to login screen on next API call. Password unchanged.', cta: 'Force logout', ctaTone: 'danger' },
          onSelect: () => post(`/admin/users/${u.id}/force-logout`) },
      ],
    },
    ...(isAdmin ? [{
      title: 'Admin only',
      items: [
        { key: 'test', icon: u.is_test ? 'science_off' : 'science',
          label: u.is_test ? 'Unmark test account' : 'Mark as test account',
          hint: u.is_test ? 'Will be counted in analytics again' : 'Will be hidden from analytics',
          onSelect: () => post(`/admin/users/${u.id}/test-flag`, { is_test: !u.is_test }) },
        { key: 'delete', icon: 'delete_forever', label: 'Delete account', tone: 'danger' as const,
          hint: 'Cascades wallet / tokens. Ludo history stays.',
          confirm: { title: `Permanently delete @${u.username}?`, body: 'This deletes the row and cascades wallet + refresh tokens. Cannot be undone.', cta: 'Delete', ctaTone: 'danger' as const },
          onSelect: async () => {
            await adminApi.del(`/admin/users/${u.id}`);
            onMutated(u);
            // SPA-stay: the list reloads via onMutated → load(), so the
            // deleted card simply disappears without a full reload.
          } },
      ],
    } satisfies ActionGroup] : []),
  ];

  return (
    <EntityActionSheet
      open
      onClose={onClose}
      title={`@${u.username}`}
      subtitle={[u.first_name, u.last_name].filter(Boolean).join(' ') || `Player #${u.id}`}
      groups={groups}
    />
  );
}

